import { useState, useEffect } from 'react';
import { PLANBASE_CONFIG } from '../config';

interface ProjectViewsMap {
  [projectId: string]: number;
}

// Local storage key for views cache
const VIEWS_CACHE_KEY = 'planbase_views_cache';

// Get all cached views
function getAllCachedViews(): { [pageId: string]: number } {
  try {
    return JSON.parse(localStorage.getItem(VIEWS_CACHE_KEY) || '{}');
  } catch {
    return {};
  }
}

// Set cached views for multiple projects
function setCachedViewsBatch(viewsMap: { [pageId: string]: number }): void {
  try {
    const cache = JSON.parse(localStorage.getItem(VIEWS_CACHE_KEY) || '{}');
    Object.assign(cache, viewsMap);
    localStorage.setItem(VIEWS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Silent fail
  }
}

export function useProjectsViews(projectIds: string[]): { viewsMap: ProjectViewsMap; loading: boolean } {
  const [viewsMap, setViewsMap] = useState<ProjectViewsMap>({});
  const [loading, setLoading] = useState(true);

  // Initialize from cache immediately (optimistic)
  useEffect(() => {
    if (projectIds.length === 0) {
      setLoading(false);
      return;
    }

    const cachedViews = getAllCachedViews();
    const initialViewsMap: ProjectViewsMap = {};
    
    projectIds.forEach(id => {
      const pageId = `project_${id}`;
      if (cachedViews[pageId] !== undefined) {
        initialViewsMap[id] = cachedViews[pageId];
      }
    });
    
    if (Object.keys(initialViewsMap).length > 0) {
      setViewsMap(initialViewsMap);
    }
  }, [projectIds.join(',')]);

  // Fetch from server and sync
  useEffect(() => {
    if (projectIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchViews = async () => {
      try {
        const pageIds = projectIds.map(id => `project_${id}`).join(',');
        
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/analytics/page-views/batch?page_ids=${encodeURIComponent(pageIds)}`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const newViewsMap: ProjectViewsMap = {};
          const cacheUpdates: { [pageId: string]: number } = {};
          
          if (data.data?.views) {
            Object.entries(data.data.views).forEach(([pageId, count]) => {
              const projectId = pageId.replace('project_', '');
              newViewsMap[projectId] = count as number;
              cacheUpdates[pageId] = count as number;
            });
          }
          
          // Update cache with server values
          setCachedViewsBatch(cacheUpdates);
          setViewsMap(newViewsMap);
        }
      } catch (error) {
        // Keep using cached values on error
      } finally {
        setLoading(false);
      }
    };

    fetchViews();
  }, [projectIds.join(',')]);

  return { viewsMap, loading };
}
